import {
  type PaginationState,
  type SortingState,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { NotesGeneratingItem } from '@eluve/api-contract';
import {
  Button,
  ColDefBuilder,
  Icon,
  Link,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ServerDataTable,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { LlmOutputTypesLookup } from '@eluve/graphql-types';

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours < 24) return `${hours}h ${remainingMinutes}m`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return `${days}d ${remainingHours}h`;
};

const getUserDisplayName = (row: NotesGeneratingItem) =>
  [row.firstName, row.lastName].filter(Boolean).join(' ') ||
  row.email ||
  'Unknown';

const ALL_OUTPUT_TYPES = 'ALL';

const columns = new ColDefBuilder<NotesGeneratingItem>()
  .defaultSortable('tenantName', 'Organization')
  .linkSortable(
    'appointmentName',
    (row) =>
      `/admin/organizations/${row.tenantId}/appointments/${row.appointmentId}/details`,
    'Appointment',
  )
  .colDef({
    id: 'user',
    header: 'User',
    accessorFn: getUserDisplayName,
    cell: ({ row }) => {
      const name = getUserDisplayName(row.original);
      if (row.original.userId) {
        return <Link to={`/admin/users/${row.original.userId}`}>{name}</Link>;
      }
      return <Text size="xs">{name}</Text>;
    },
    enableSorting: true,
  })
  .defaultSortable('jobStatus', 'Status')
  .defaultSortable('llmOutputType', 'Output Type')
  .colDef({
    id: 'promptTemplate',
    header: 'Prompt Template',
    accessorKey: 'promptTemplateName',
    cell: ({ row }) => {
      const name = row.original.promptTemplateName;
      return (
        <Text size="xs" color={name ? 'primary' : 'tertiary'}>
          {name ?? 'N/A'}
        </Text>
      );
    },
    enableSorting: true,
  })
  .colDef({
    id: 'duration',
    header: 'Duration',
    accessorKey: 'durationSeconds',
    cell: ({ row }) => (
      <Text size="xs">{formatDuration(row.original.durationSeconds)}</Text>
    ),
    enableSorting: true,
  })
  .dateSortable('createdAt', 'Created At')
  .dateSortable('jobCreatedAt', 'Started At')
  .build();

const AUTO_REFRESH_INTERVAL_MS = 30_000;

export const NotesGeneratingTab: React.FC = () => {
  const api = useApiClient();
  const [data, setData] = useState<NotesGeneratingItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fetchDurationMs, setFetchDurationMs] = useState<number | null>(null);
  const toast = useToast();
  const [outputTypeFilter, setOutputTypeFilter] = useState(ALL_OUTPUT_TYPES);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'duration', desc: true },
  ]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const startTime = performance.now();
    const sort = sorting[0];
    const result = await api.adminOnCall.getNotesGenerating({
      query: {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        sortBy: sort?.id,
        sortDirection: sort?.desc ? 'desc' : 'asc',
        outputType:
          outputTypeFilter === ALL_OUTPUT_TYPES ? undefined : outputTypeFilter,
      },
    });
    const elapsed = Math.round(performance.now() - startTime);
    setFetchDurationMs(elapsed);
    if (result.status === 200) {
      setData(result.body.items);
      setTotalCount(result.body.totalCount);
    } else {
      toast.error('Failed to load generating notes. Please try again.');
    }
    setLoading(false);
  }, [
    api,
    outputTypeFilter,
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
  ]);

  useEffect(() => {
    fetchData();

    intervalRef.current = setInterval(fetchData, AUTO_REFRESH_INTERVAL_MS);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchData]);

  const outputTypes = useMemo(
    () => Object.values(LlmOutputTypesLookup).sort(),
    [],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: Math.ceil(totalCount / pagination.pageSize),
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
  });

  return (
    <Stack gap={2}>
      <Stack gap={2} direction="horizontal">
        <Select value={outputTypeFilter} onValueChange={setOutputTypeFilter}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="Filter by output type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_OUTPUT_TYPES}>All Output Types</SelectItem>
            {outputTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Stack
          className="ml-auto"
          fullWidth={false}
          gap={2}
          direction="horizontal"
        >
          <Text inline size="xs" color="tertiary">
            {loading
              ? 'Loading...'
              : `${totalCount} job${totalCount !== 1 ? 's' : ''} generating${fetchDurationMs !== null ? ` · ${fetchDurationMs >= 1000 ? `${(fetchDurationMs / 1000).toFixed(1)}s` : `${fetchDurationMs}ms`}` : ''} · Auto-refreshes every 30s`}
          </Text>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <Icon name="RefreshCw" size="auto" />
            Refresh
          </Button>
        </Stack>
      </Stack>
      <Stack className="rounded-md border">
        <ServerDataTable<NotesGeneratingItem>
          table={table}
          isPending={loading}
          placeholder="No notes currently generating."
        />
      </Stack>
    </Stack>
  );
};
